import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { TPlainObject } from '@/lib/types';
import type { AdminUsersListParams } from '@/types';
import type {
  CreateRoleDto,
  Permission,
  Role,
  UpdateRoleDto,
  UpdateUserDto,
  User,
  UserDetails,
} from '@/types/admin';

// ============================================================================
// Types
// ============================================================================

export type PaginatedUsersResponse = {
  items: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type RolesResponse = {
  roles: Role[];
};

export type PermissionsResponse = {
  permissions: Permission[];
};

export type UserRolesResponse = {
  userId: number;
  roles: any[];
};

// ============================================================================
// Query Keys
// ============================================================================

export const adminUserKeys = {
  all: ['admin-users'] as const,
  lists: () => [...adminUserKeys.all, 'list'] as const,
  list: (params?: AdminUsersListParams) =>
    [...adminUserKeys.lists(), params] as const,
  details: () => [...adminUserKeys.all, 'detail'] as const,
  detail: (id: number) => [...adminUserKeys.details(), id] as const,
  roles: (id: number) => [...adminUserKeys.all, 'roles', id] as const,
};

export const adminRoleKeys = {
  all: ['admin-roles'] as const,
  lists: () => [...adminRoleKeys.all, 'list'] as const,
  list: () => [...adminRoleKeys.lists()] as const,
};

export const adminPermissionKeys = {
  all: ['admin-permissions'] as const,
  lists: () => [...adminPermissionKeys.all, 'list'] as const,
  list: () => [...adminPermissionKeys.lists()] as const,
};

// ============================================================================
// API Functions
// ============================================================================

async function fetchUsers(
  params?: AdminUsersListParams,
): Promise<PaginatedUsersResponse> {
  const url = `admin/users/list`;
  const response = await call<PaginatedUsersResponse>(url, {
    method: 'GET',
    json: params as TPlainObject,
  });
  return response.json();
}

async function fetchUserDetails(id: number): Promise<UserDetails> {
  const response = await call<UserDetails>(`admin/users/details`, {
    json: { id },
  });
  return response.json();
}

async function updateUserApi(
  data: UpdateUserDto,
): Promise<{ success: boolean; user: User }> {
  const response = await call<{ success: boolean; user: User }>(
    'admin/users/update',
    {
      json: data,
    },
  );
  return response.json();
}

async function fetchRoles(): Promise<RolesResponse> {
  const response = await call<RolesResponse>('rbac/roles', {
    method: 'GET',
  });
  return response.json();
}

async function createRoleApi(
  data: CreateRoleDto,
): Promise<{ success: boolean; role: Role }> {
  const response = await call<{ success: boolean; role: Role }>(
    'rbac/roles/create',
    {
      json: data,
    },
  );
  return response.json();
}

async function updateRoleApi(
  data: UpdateRoleDto,
): Promise<{ success: boolean; role: Role }> {
  const response = await call<{ success: boolean; role: Role }>(
    'rbac/roles/update',
    {
      json: data,
    },
  );
  return response.json();
}

async function deleteRoleApi(
  roleId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/roles/delete',
    {
      json: { roleId },
    },
  );
  return response.json();
}

async function fetchUserRoles(userId: number): Promise<UserRolesResponse> {
  const response = await call<UserRolesResponse>(`rbac/user-roles`, {
    json: { id: userId },
  });
  return response.json();
}

async function assignRoleApi(
  userId: number,
  roleId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-roles/assign',
    {
      json: { userId, roleId },
    },
  );
  return response.json();
}

async function revokeRoleApi(
  userId: number,
  roleId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-roles/revoke',
    {
      json: { userId, roleId },
    },
  );
  return response.json();
}

async function fetchAllPermissions(): Promise<PermissionsResponse> {
  const response = await call<PermissionsResponse>('rbac/permissions', {
    method: 'GET',
  });
  return response.json();
}

async function grantPermissionApi(
  userId: number,
  permissionName: string,
): Promise<{ success: boolean; message: string }> {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-permissions/grant',
    {
      json: { userId, permissionName },
    },
  );
  return response.json();
}

async function revokePermissionApi(
  userId: number,
  permissionName: string,
): Promise<{ success: boolean; message: string }> {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-permissions/revoke',
    {
      json: { userId, permissionName },
    },
  );
  return response.json();
}

async function removePermissionOverrideApi(
  userId: number,
  permissionName: string,
): Promise<{ success: boolean; message: string }> {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-permissions/remove',
    {
      json: { userId, permissionName },
    },
  );
  return response.json();
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch users with pagination support
 */
export function useAdminUsers(params?: AdminUsersListParams) {
  return useQuery({
    queryKey: adminUserKeys.list(params),
    queryFn: () => fetchUsers(params),
  });
}

/**
 * Fetch detailed user information including roles and permissions
 */
export function useUserDetails(userId: number | undefined) {
  return useQuery({
    queryKey: adminUserKeys.detail(userId!),
    queryFn: () => fetchUserDetails(userId!),
    enabled: !!userId,
  });
}

/**
 * Fetch all roles
 */
export function useRoles() {
  return useQuery({
    queryKey: adminRoleKeys.list(),
    queryFn: fetchRoles,
  });
}

/**
 * Fetch all permissions
 */
export function useAllPermissions() {
  return useQuery({
    queryKey: adminPermissionKeys.list(),
    queryFn: fetchAllPermissions,
  });
}

/**
 * Fetch roles assigned to a specific user
 */
export function useUserRoles(userId: number | undefined) {
  return useQuery({
    queryKey: adminUserKeys.roles(userId!),
    queryFn: () => fetchUserRoles(userId!),
    enabled: !!userId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Update user information
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserApi,
    onSuccess: (_, variables) => {
      notifications.show({
        title: 'Success',
        message: 'User updated successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.detail(variables.userId),
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to update user',
        color: 'red',
      });
    },
  });
}

/**
 * Create a new role
 */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRoleApi,
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Role created successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.all });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to create role',
        color: 'red',
      });
    },
  });
}

/**
 * Update an existing role
 */
export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateRoleApi,
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Role updated successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.all });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to update role',
        color: 'red',
      });
    },
  });
}

/**
 * Delete a role
 */
export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRoleApi,
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Role deleted successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.all });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to delete role',
        color: 'red',
      });
    },
  });
}

/**
 * Assign a role to a user
 */
export function useAssignRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: number; roleId: number }) =>
      assignRoleApi(userId, roleId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.detail(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.roles(variables.userId),
      });
    },
  });
}

/**
 * Revoke a role from a user
 */
export function useRevokeRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: number; roleId: number }) =>
      revokeRoleApi(userId, roleId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.detail(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.roles(variables.userId),
      });
    },
  });
}

/**
 * Grant an explicit permission to a user
 */
export function useGrantPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      permissionName,
    }: {
      userId: number;
      permissionName: string;
    }) => grantPermissionApi(userId, permissionName),
    onSuccess: (_, variables) => {
      notifications.show({
        title: 'Success',
        message: 'Permission granted successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.detail(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to grant permission',
        color: 'red',
      });
    },
  });
}

/**
 * Revoke a permission from a user
 */
export function useRevokePermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      permissionName,
    }: {
      userId: number;
      permissionName: string;
    }) => revokePermissionApi(userId, permissionName),
    onSuccess: (_, variables) => {
      notifications.show({
        title: 'Success',
        message: 'Permission revoked successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.detail(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to revoke permission',
        color: 'red',
      });
    },
  });
}

/**
 * Remove an explicit permission override
 */
export function useRemovePermissionOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      permissionName,
    }: {
      userId: number;
      permissionName: string;
    }) => removePermissionOverrideApi(userId, permissionName),
    onSuccess: (_, variables) => {
      notifications.show({
        title: 'Success',
        message: 'Permission override removed successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.detail(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to remove permission override',
        color: 'red',
      });
    },
  });
}
